import { useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { Download, Upload } from 'lucide-react';
import {
  type BulkCreateProductGroupEntryPayload,
  type BulkCreateProductGroupsResponse,
} from '../../../../../infrastructure/ProductGroupClient';
import { MAX_CSV_DATA_ROWS, parseProductGroupsCsv } from '../../utils/parseProductGroupsCsv';
import { CsvDropzone, type CsvDropzoneHandle } from './CsvDropzone';

const SAMPLE_CSV_FILENAME = 'product-groups-sample.csv';
const SAMPLE_CSV_BODY = `groupName,color,size,sku
"Sample 1D Group","21 Ivory",,"SKU-A1"
"Sample 1D Group","23 Sand",,"SKU-A2"
"Sample 2D Group","Red","S","SKU-B1"
"Sample 2D Group","Red","L","SKU-B2"
"Sample 2D Group","Pink","S","SKU-B3"
`;

interface CsvUploadModalProps {
  show: boolean;
  onHide: () => void;
  onUpload: (groups: BulkCreateProductGroupEntryPayload[]) => Promise<BulkCreateProductGroupsResponse>;
}

export function CsvUploadModal({ show, onHide, onUpload }: CsvUploadModalProps) {
  const dropzoneRef = useRef<CsvDropzoneHandle>(null);
  const [parsedGroups, setParsedGroups] = useState<BulkCreateProductGroupEntryPayload[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkCreateProductGroupsResponse | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const reset = () => {
    setParsedGroups([]);
    setParseErrors([]);
    setUploading(false);
    setUploadResult(null);
    setSelectedFileName(null);
    dropzoneRef.current?.reset();
  };

  const handleClose = () => {
    if (uploading) {
      return;
    }
    reset();
    onHide();
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV_BODY], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = SAMPLE_CSV_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = async (file: File) => {
    setUploadResult(null);
    setSelectedFileName(file.name);
    try {
      const text = await file.text();
      const { groups, errors } = parseProductGroupsCsv(text);
      setParsedGroups(groups);
      setParseErrors(errors);
    } catch (readError) {
      setParseErrors([readError instanceof Error ? readError.message : 'Failed to read file']);
      setParsedGroups([]);
    }
  };

  const handleUpload = async () => {
    if (parsedGroups.length === 0) {
      return;
    }
    setUploading(true);
    try {
      const bulkCreateResult = await onUpload(parsedGroups);
      setUploadResult(bulkCreateResult);
      const summaryMessage = `Imported ${bulkCreateResult.createdCount} created, ${bulkCreateResult.failedCount} failed`;
      if (bulkCreateResult.createdCount === 0 && bulkCreateResult.failedCount > 0) {
        toast.error(summaryMessage);
      } else if (bulkCreateResult.failedCount > 0) {
        toast(summaryMessage);
      } else {
        toast.success(summaryMessage);
      }
    } catch (uploadError) {
      toast.error(uploadError instanceof Error ? uploadError.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const canUpload = parsedGroups.length > 0 && parseErrors.length === 0 && !uploading && uploadResult === null;

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop={uploading ? 'static' : true}>
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>Upload product groups (CSV)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fw-semibold small mb-2">Tips</div>
          <ul className="text-muted small mb-0 ps-3">
            <li>One row per product.</li>
            <li>
              First column is <code>groupName</code>, last column is <code>sku</code>. In between, add one column per
              attribute (e.g. <code>color</code>, <code>size</code>).
            </li>
            <li>
              Rows with the same <code>groupName</code> become one group.
            </li>
            <li>
              CSV only <strong>creates new groups</strong>. To change an existing group, use the editor.
            </li>
            <li>
              Up to <strong>{MAX_CSV_DATA_ROWS}</strong> rows per upload — split larger files into multiple uploads.
            </li>
          </ul>
        </div>

        <CsvDropzone
          ref={dropzoneRef}
          selectedFileName={selectedFileName}
          uploading={uploading}
          onFileChange={(file) => void handleFileChange(file)}
        />

        {parseErrors.length > 0 && (
          <Alert variant="danger">
            <div className="fw-semibold mb-1">CSV parse errors:</div>
            <ul className="mb-0 small">
              {parseErrors.map((parseError, parseErrorIndex) => (
                <li key={parseErrorIndex}>{parseError}</li>
              ))}
            </ul>
          </Alert>
        )}

        {parsedGroups.length > 0 && parseErrors.length === 0 && !uploadResult && (
          <Alert variant="success">
            Ready to import <strong>{parsedGroups.length}</strong> product group
            {parsedGroups.length === 1 ? '' : 's'}.
          </Alert>
        )}

        {uploadResult && (
          <div className="mt-3">
            <Alert variant={uploadResult.failedCount === 0 ? 'success' : 'secondary'}>
              Created: <strong>{uploadResult.createdCount}</strong>, Failed: <strong>{uploadResult.failedCount}</strong>
            </Alert>
            <Table size="sm" className="small">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Outcome</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {uploadResult.productGroups.map((entry, entryIndex) => (
                  <tr key={entryIndex}>
                    <td>{entry.groupName}</td>
                    <td>
                      <span
                        className={entry.status === 'failed' ? 'text-danger fw-semibold' : 'text-success fw-semibold'}
                      >
                        {entry.status}
                      </span>
                    </td>
                    <td className="text-muted">
                      {entry.error ??
                        (entry.failedSkus.length > 0 ? `failedSkus: ${entry.failedSkus.join(', ')}` : '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={downloadSample} className="me-auto">
          <Download size={14} className="me-1" />
          Download sample CSV
        </Button>
        <Button variant="outline-secondary" onClick={handleClose} disabled={uploading}>
          Close
        </Button>
        <Button variant="dark" onClick={handleUpload} disabled={!canUpload}>
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Uploading…
            </>
          ) : (
            <>
              <Upload size={14} className="me-1" />
              Upload {parsedGroups.length > 0 ? `(${parsedGroups.length})` : ''}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
